import { useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import {
    EllipsisDropdownItem,
    NoBorderButtonWrap,
    Popover,
    PopoverTrigger,
    SimpleChipItemWrap,
    DISABLE_TOOLTIP_VALUE,
    SearchInput,
} from "@similarweb/ui-components";

import { usePrevious } from "app/components/hooks/usePrevious";
import TechnologiesService from "app/pages/sales-intelligence/modules/technology/services/company-analysis-technologies-filter/technologiesService";
import type {
    CompanyAnalysisTechnologyItem,
    CompanyAnalysisTechnologyOption,
} from "app/pages/sales-intelligence/modules/technology/types/company-analysis-technology-filter";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import { FooterButton } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";
import { getDropdownTitle } from "pro-features/components/MultiChipDown/src/MultiChipDown";
import { Operator } from "pro-features/components/MultiChipDown/src/MultiChipDownTypes";

import {
    StyledPopoverContent,
    StyledOptionsList,
    StyledPopoverTrigger,
    StyledSearchTechnologiesContainer,
} from "./styles";

export const TechnologiesCategoryFilter = ({
    technologies,
    onDone,
}: {
    technologies: CompanyAnalysisTechnologyItem[] | undefined;
    onDone: (categories: CompanyAnalysisTechnologyOption[]) => void;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState<CompanyAnalysisTechnologyOption[]>([]);
    const prevSelectedItems = usePrevious(selectedItems);

    const [searchTerm, setSearchTerm] = useState<string>("");

    const [showApplyButton, setShowApplyButton] = useState(false);
    const [chipTitle, setChipTitle] = useState<string>("");
    const [chipTooltipText, setChipTooltipText] = useState<string>("");

    const initialOptions = useMemo(
        () => (technologies ? TechnologiesService.convertTechnologiesToOptions(technologies) : []),
        [technologies],
    );

    const options = useMemo(() => {
        const searchWords = searchTerm.toLowerCase().split(" ");
        if (!searchTerm || searchWords.length === 0) {
            return initialOptions;
        }
        return initialOptions.filter(({ text }) => searchWords.every((word) => text.toLowerCase().includes(word)));
    }, [initialOptions, searchTerm]);

    const onApply = () => {
        onDone(selectedItems);
        const itemsTitles = selectedItems.filter((item) => item.isChild).map((item) => item?.text);
        setChipTooltipText(itemsTitles.join(", "));
        setChipTitle(getDropdownTitle(itemsTitles, Operator.Or));
        setIsOpen(false);
        setSearchTerm("");
        setShowApplyButton(false);
    };

    const handleChildClick = (selectedOption: CompanyAnalysisTechnologyOption, isChecked: boolean) => {
        const updatedItems = isChecked
            ? selectedItems.filter((item) => item.id !== selectedOption.id)
            : [...selectedItems, selectedOption];

        setSelectedItems(updatedItems);
        setShowApplyButton(true);
    };

    const handleParentClick = (selectedOption: CompanyAnalysisTechnologyOption, isChecked: boolean) => {
        const parentIdentifier = selectedOption.id.substring(0, selectedOption.id.lastIndexOf("_parent"));
        const relatedOptions = initialOptions.filter((option) => option.parent === parentIdentifier);
        const relatedIds = relatedOptions.map((option) => option.id);

        const updatedItems = isChecked
            ? selectedItems.filter((item) => !relatedIds.includes(item.id))
            : [...selectedItems, ...relatedOptions];
        setSelectedItems(updatedItems);
        setShowApplyButton(true);
    };

    const clearSelection = () => {
        setSelectedItems([]);
        onDone([]);
        setChipTitle("");
    };

    const renderDropDownItem = (option: CompanyAnalysisTechnologyOption) => {
        const { text, isChild, count, items, id } = option;
        const isSelected = isChild
            ? !!selectedItems.find((item) => item.id === id)
            : items.every((item) => selectedItems.find((selectedItem) => selectedItem.id === `${item}_child`));
        const partiallySelected = isChild
            ? false
            : items.some((item) => selectedItems.find((selectedItem) => selectedItem.id === `${item}_child`));
        return (
            <EllipsisDropdownItem
                showCheckBox
                key={id}
                iconName={isChild ? undefined : "category"}
                iconSize="sm"
                iconColor={colorsPalettes.carbon[200]}
                partiallySelected={partiallySelected}
                infoText={count}
                textIndent={isChild}
                selected={!!isSelected}
                onClick={() => (isChild ? handleChildClick(option, isSelected) : handleParentClick(option, isSelected))}
            >
                {text}
            </EllipsisDropdownItem>
        );
    };

    const handleToggle = (open: boolean) => {
        setIsOpen(open);
        if (!open) {
            setSelectedItems(prevSelectedItems || []);
            setShowApplyButton(false);
            setSearchTerm("");
        }
    };

    const handleSearch = (term: string) => {
        setSearchTerm(term);
    };

    return (
        <Popover
            isClickOutsideEnabled
            open={isOpen}
            onOpenChange={handleToggle}
            placement="bottom-start"
        >
            <PopoverTrigger asChild>
                <StyledPopoverTrigger>
                    {chipTitle && chipTitle.length ? (
                        <SimpleChipItemWrap
                            key="firstItem"
                            text={chipTitle}
                            tooltipText={chipTooltipText}
                            onCloseItem={clearSelection}
                            onClick={() => setIsOpen(true)}
                            tooltipMaxWidth={230}
                            displayTooltipForTextLongerThan={selectedItems.length > 2 ? 1 : DISABLE_TOOLTIP_VALUE}
                            enabledTooltip={selectedItems.length > 2}
                        />
                    ) : (
                        <div onClick={() => setIsOpen(true)}>
                            <NoBorderButtonWrap
                                disabled={!technologies || technologies.length === 0}
                                key="selectedItem"
                            >
                                {translate("si.company-analysis.technologies.card.filter.button")}
                            </NoBorderButtonWrap>
                        </div>
                    )}
                </StyledPopoverTrigger>
            </PopoverTrigger>
            <StyledPopoverContent>
                <StyledSearchTechnologiesContainer>
                    <SearchInput
                        disableClear={true}
                        onChange={handleSearch}
                        placeholder={translate("si.company-analysis.technologies.card.filter.placeholder")}
                    />
                </StyledSearchTechnologiesContainer>
                <StyledOptionsList>
                    {options.length > 0 ? (
                        options.map((option) => renderDropDownItem(option))
                    ) : (
                        <EllipsisDropdownItem>
                            <I18n>dropdown.no.results</I18n>
                        </EllipsisDropdownItem>
                    )}
                </StyledOptionsList>
                {showApplyButton ? (
                    <FooterButton onClick={onApply}>
                        <I18n>common.apply</I18n>
                    </FooterButton>
                ) : null}
            </StyledPopoverContent>
        </Popover>
    );
};
